import type { OntologyVersion } from './types/ontology.js';

/**
 * How a version row is named in the UI.
 *
 * WHY this exists: a draft has no `version_number` (migration 043), so every
 * renderer had to decide independently what to show. Left to each call site,
 * that decision drifts into inventing a number ("v" + undefined, or the parent's
 * number shown as if it were the draft's), which is exactly the confusion the
 * schema change removed. One function, one wording.
 *
 * @param version   the row to label
 * @param parentNumber  version_number of `version.parent_version_id`, when the
 *   caller has it. Only used for drafts, to say what the draft is based on.
 */
export function versionLabel(
  version: Pick<OntologyVersion, 'version_number' | 'status'>,
  parentNumber?: string | null
): string {
  if (version.status === 'published') {
    // A published row without a number means the publish path was bypassed;
    // saying so is better than printing "vnull".
    return version.version_number ? `v${version.version_number}` : 'Published (unnumbered)';
  }
  return parentNumber ? `Draft (based on v${parentNumber})` : 'Draft (unpublished)';
}

/**
 * Label for a draft when only the version list is available: resolves the parent
 * number from the list instead of making the caller do the lookup.
 */
export function draftLabelFromList(
  draft: Pick<OntologyVersion, 'version_number' | 'status' | 'parent_version_id'>,
  all: ReadonlyArray<Pick<OntologyVersion, 'id' | 'version_number'>>
): string {
  const parent = draft.parent_version_id
    ? all.find((v) => v.id === draft.parent_version_id)
    : undefined;
  return versionLabel(draft, parent?.version_number ?? null);
}
